<template>
  <div ref="mapEl" class="terrain-location-picker-map" role="img" aria-label="Mapa para confirmar o ajustar la ubicación"></div>
</template>

<script>
// LocationPickerMap shows a single point on a minimal OpenLayers map and lets
// the respondent tap anywhere to move it — the manual-correction affordance
// LocationField.vue already anticipated (its `correct` event) but that was
// never wired to an actual picker anywhere in the app until now. Coordinates
// are always [lon, lat] in EPSG:4326 at the component boundary; OL's
// internal projection (EPSG:3857) never leaks to the parent.
//
// Deliberately not the shared app map (mapService.js): that is a singleton
// bound to a fixed target and loaded with WMS/print/annotation machinery
// this modal doesn't need — a second, disposable Map instance is simpler and
// avoids fighting over the singleton.
import Map from "ol/Map.js";
import View from "ol/View.js";
import TileLayer from "ol/layer/Tile.js";
import OSM from "ol/source/OSM.js";
import VectorLayer from "ol/layer/Vector.js";
import VectorSource from "ol/source/Vector.js";
import Feature from "ol/Feature.js";
import Point from "ol/geom/Point.js";
import { Style, Icon } from "ol/style.js";
import { fromLonLat, toLonLat } from "ol/proj.js";

// Self-contained teardrop pin (data URI, no external asset/network
// dependency, unlike the marker icon Locator.vue loads from a public GitHub
// URL). Anchored at the bottom tip so it points exactly at the coordinate.
const PIN_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">' +
    '<path d="M16 0C7.163 0 0 7.163 0 16c0 11 16 26 16 26s16-15 16-26C32 7.163 24.837 0 16 0z" fill="#E8730C"/>' +
    '<circle cx="16" cy="15" r="6" fill="#ffffff"/>' +
    "</svg>"
);
const PIN_ICON_SRC = `data:image/svg+xml;charset=utf-8,${PIN_SVG}`;

// DEFAULT_ZOOM 16 favors context (nearby streets/landmarks the respondent can
// recognize) over raw precision: at zoom 17+ the map reads as a close-up
// crop with little to orient by, which matters in terrain fieldwork where
// surroundings are often sparse. 16 still resolves a ~50m (advertida)
// accuracy radius clearly on a 280px-tall map.
const DEFAULT_ZOOM = 16;

export default {
  name: "LocationPickerMap",
  props: {
    // [lon, lat] in EPSG:4326.
    coordinates: { type: Array, required: true },
    // readonly disables the tap-to-move interaction — used by the reviewer
    // detail view (TerrainReviewQueue.vue), which only displays where a
    // Respuesta was captured and must not let a reviewer silently "move" it.
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      map: null,
      markerFeature: null,
      // Coordinates just emitted by our own click handler: the following
      // `coordinates` prop update it produces upstream is an echo, not a
      // new external position, so the view must not react to it (that
      // round-trip was causing the recenter-on-click jump).
      lastEmitted: null,
    };
  },
  mounted() {
    const center = fromLonLat(this.coordinates);

    this.markerFeature = new Feature({ geometry: new Point(center) });
    this.markerFeature.setStyle(
      new Style({
        image: new Icon({ src: PIN_ICON_SRC, anchor: [0.5, 1], scale: 1 }),
      })
    );

    const markerLayer = new VectorLayer({
      source: new VectorSource({ features: [this.markerFeature] }),
      zIndex: 10,
    });

    this.map = new Map({
      target: this.$refs.mapEl,
      layers: [new TileLayer({ source: new OSM() }), markerLayer],
      view: new View({ center, zoom: DEFAULT_ZOOM }),
    });

    if (!this.readonly) {
      this.map.on("click", (event) => {
        const coords = toLonLat(event.coordinate);
        this.markerFeature.getGeometry().setCoordinates(event.coordinate);
        this.lastEmitted = coords;
        this.$emit("update:coordinates", coords);
      });
    }
  },
  beforeDestroy() {
    if (this.map) this.map.setTarget(null);
  },
  watch: {
    coordinates(value) {
      if (!this.map || !this.markerFeature) return;
      if (this.lastEmitted && value[0] === this.lastEmitted[0] && value[1] === this.lastEmitted[1]) {
        this.lastEmitted = null;
        return;
      }
      // A genuinely new external position (e.g. a fresh GPS fix): move the
      // marker and ease the view over, rather than the instant recenter a
      // plain setCenter would produce.
      const projected = fromLonLat(value);
      this.markerFeature.getGeometry().setCoordinates(projected);
      this.map.getView().animate({ center: projected, duration: 300 });
    },
  },
};
</script>

<style scoped>
.terrain-location-picker-map {
  width: 100%;
  height: 280px;
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  overflow: hidden;
}
</style>
